import _classCallCheck from "/home/runner/_work/mono/mono/node_modules/.pnpm/@babel+runtime@7.9.0/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/home/runner/_work/mono/mono/node_modules/.pnpm/@babel+runtime@7.9.0/node_modules/@babel/runtime/helpers/esm/createClass";
import { prop } from 'ramda';
import { passThrough, throttle } from '../utils/promise-utils';
import { urls } from './../config.js';
import { axios } from './../utils/axios.js';

/**
 * This is used by our auth service to store session details. It has subscribe
 * functionality if anything in your application also needs to be aware of
 * changes here. After instantiation this should be considered read only (via
 * subscribe), as making changes to it's contents _will_ disrupt the
 * authentication of api calls.
 *
 * This will not persist between browser reloads, so if you're working in a
 * browser and require persistence, you should subscribe to the update methods
 * and store credentials using your application storage method. On reload you
 * can then re-instantiate a session with the saved credentials and inject that
 * into auth.
 *
 * Basic example:
 * ```
 * const CLIENT_ID = "Your client id";
 * const CLIENT_SECRET = "Your client secret";
 * const getPersistedTokens = () => {[your code]};
 * const persistTokens = ({ clientToken, userToken, refreshToken, userId }) =>
 *  {[your code]};
 *
 * getPersistedTokens().then(([userId, token, refreshToken, clientToken]) => {
 *  const session = new Session(
 *    CLIENT_ID,
 *    CLIENT_SECRET,
 *    clientToken,
 *    userId,
 *    token,
 *    refreshToken
 *  );
 *  session.subscribe('some-unique-id', persistTokens);
 *  apiAuth = new Auth(session);
 *});
 *```
 */
var Session = /*#__PURE__*/function () {
  function Session(clientId, clientSecret) {
    var clientToken = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
    var userId = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : null;
    var userToken = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : null;
    var refreshToken = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : null;
    var userTokenExpiry = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : null;
    _classCallCheck(this, Session);
    this.clientId = clientId;
    this.clientSecret = clientSecret;
    this.clientToken = clientToken;
    this.userId = userId;
    this.userToken = userToken;
    this.refreshToken = refreshToken;
    this.userTokenExpiry = userTokenExpiry;
    this.subscribers = {};
    this._generateClientToken = throttle(this.__generateClientToken.bind(this));
    this._refreshUserToken = throttle(this.__refreshUserToken.bind(this));
  }

  /**
   * Makes a call to the api to generate a client token.
   *
   * This method is throttled in the construct to a single-underscored version.
   *
   * It is the throttled method which should be called.
   *
   * @private
   */
  return _createClass(Session, [{
    key: "__generateClientToken",
    value: function __generateClientToken() {
      var _this = this;
      return axios({
        autoAuth: false
      }).then(function (api) {
        return api.post(urls.api('/oauth/token'), {
          client_id: _this.clientId,
          client_secret: _this.clientSecret,
          grant_type: 'client_credentials'
        });
      }).then(prop('data')).then(prop('access_token')).then(passThrough(this._updateClientToken.bind(this))).catch(function (err) {
        _this._updateClientToken(null);
        return Promise.reject(err);
      });
    }

    /**
     * notifies subscribers when the session has updated.
     *
     * @private
     */
  }, {
    key: "_updated",
    value: function _updated() {
      var _this2 = this;
      Object.keys(this.subscribers).forEach(function (key) {
        return _this2.subscribers[key]({
          clientId: _this2.clientId,
          clientToken: _this2.clientToken,
          userId: _this2.userId,
          userToken: _this2.userToken,
          userTokenExpiry: _this2.userTokenExpiry,
          refreshToken: _this2.refreshToken
        });
      });
    }

    /**
     * Returns a promise which resolves with the current client token, or
     * generates one if one should not exist.
     *
     * @private
     */
  }, {
    key: "_getClientToken",
    value: function _getClientToken() {
      return this.clientToken ? Promise.resolve(this.clientToken) : this._generateClientToken();
    }

    /**
     * Returns a promise which resolves with the current user token, or rejects if
     * one should not exist.
     *
     * @private
     */
  }, {
    key: "_getUserToken",
    value: function _getUserToken() {
      return this.userToken ? Promise.resolve(this.userToken) : Promise.reject(new Error('no token'));
    }

    /**
     * @private
     */
  }, {
    key: "_updateClientToken",
    value: function _updateClientToken(token) {
      this.clientToken = token;
      this._updated();
    }

    /**
     * Called by our internal auth on logout. If you handle logout activities
     * elsewhere in the app you may wish to call this, but it *should usually be
     * proxied through the auth service only*
     */
  }, {
    key: "clearUser",
    value: function clearUser() {
      this.userId = null;
      this.userToken = null;
      this.userTokenExpiry = null;
      this.refreshToken = null;
      this._updated();
    }

    /**
     * Called by our internal auth on log in. If you handle log in activities
     * elsewhere in the app you may wish to call this, but it *should usually be
     * proxied through the auth service only*
     */
  }, {
    key: "updateUser",
    value: function updateUser(_ref) {
      var access_token = _ref.access_token,
        refresh_token = _ref.refresh_token,
        expires_in = _ref.expires_in,
        user_id = _ref.user_id;
      this.userId = user_id;
      this.userToken = access_token;
      this.userTokenExpiry = expires_in;
      this.refreshToken = refresh_token;
      this._updated();
    }

    /**
     * Attempts to retrieve a priveleged user token, and if that should fail will
     * fall back to returning a less priveleged client token.
     *
     */
  }, {
    key: "getToken",
    value: function getToken() {
      var _this3 = this;
      return this._getUserToken().catch(function () {
        return _this3._getClientToken();
      });
    }

    /**
     * Use this method to be notified on change of the session.
     *
     * This is typically used for persisting tokens between page reloads etc.
     *
     * @param {string} id The id of your subscriber should you ever need to
     *                    deregister it
     * @param {Function} subscriber a function to be called on update.
     * @throws {Error} if subscriber is not a function
     */
  }, {
    key: "subscribe",
    value: function subscribe(id, subscriber) {
      if (typeof subscriber !== 'function') {
        throw new Error('Subscriber must be a function.');
      }
      this.subscribers[id] = subscriber;
    }

    /**
     * This will deregister a subscriber. This should be called during teardown
     * of any modules which registers via `subscribe()`.
     * @param {string} id The id of the subscriber to deregister as passed to
     *                    `subscribe()`
     */
  }, {
    key: "unsubscribe",
    value: function unsubscribe(id) {
      Reflect.deleteProperty(this.subscribers, id);
    }

    /**
     * If you are making api calls manually elsewhere and receive a 401 it's
     * probably time to refresh your user tokens! If so this method will do that
     * for you.
     *
     * In general this is handled by interceptors in our api implementation and
     * should be handled automatically for you.
     *
     * @returns {Promise<object|error>} object contains tokens and user id
     * @private
     */
  }, {
    key: "__refreshUserToken",
    value: function __refreshUserToken() {
      var _this4 = this;
      return axios({
        autoAuth: false
      }).then(function (api) {
        return api.request({
          url: urls.api('/oauth/token'),
          method: 'post',
          data: {
            grant_type: 'refresh_token',
            refresh_token: _this4.refreshToken,
            client_id: _this4.clientId,
            client_secret: _this4.clientSecret
          }
        }).then(prop('data')).then(function (_ref2) {
          var refresh_token = _ref2.refresh_token,
            access_token = _ref2.access_token,
            expires_in = _ref2.expires_in;
          return {
            refresh_token: refresh_token,
            access_token: access_token,
            expires_in: expires_in,
            user_id: _this4.userId
          };
        }).then(passThrough(_this4.updateUser.bind(_this4)));
      });
    }
  }, {
    key: "refreshTokens",
    value: function refreshTokens() {
      var _this5 = this;
      if (this.userToken && this.refreshToken) {
        return this._refreshUserToken().catch(function () {
          // if we can't refresh we no longer have a user so clear it.
          _this5.clearUser();
          // Assuming transition from user-auth to client-auth we'll likely need a
          // new client token. So let's just generate one now and save triggering
          // yet another interceptor in the next request.
          // (This is mostly a complexity-reduction rather than performance-fix.)
          return _this5._generateClientToken();
        });
      }
      // we can occasionally get stuck in a situation where we have a temporary user token, but no
      // refresh token. If the user token then expires, that user should be cleared as invalid.
      this.clearUser();
      return this._generateClientToken();
    }
  }]);
}();
export default Session;